import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import api from '../utils/api';

import '../css/FloorplanViewer.css';      // (왕자님 공용 CSS)
import '../css/DevicePositionEditor.css'; // (우상단 바 전용 CSS)

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');

export default function DevicePositionEditor() {
  const navigate = useNavigate();
  const { locationName, floorName } = useParams();

  const [location, setLocation] = useState(null);
  const [floor, setFloor] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [draggingId, setDraggingId] = useState(null);
  const [changed, setChanged] = useState(() => new Map());
  const [saving, setSaving] = useState(false);
  const [showChanges, setShowChanges] = useState(false); // 변경목록 드롭다운

  const imgRef = useRef(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');

        const locRes = await api.get('/location', { params: { name: locationName } });
        if (abort) return;
        const loc = locRes.data || null;
        setLocation(loc);

        const f = Array.isArray(loc?.floors)
          ? loc.floors.find((x) => x.name === floorName) || loc.floors[0]
          : null;
        setFloor(f);

        if (loc?.name && f?.name) {
          const devRes = await api.get('/devices', {
            params: { location: loc.name, floor: f.name },
          });
          if (abort) return;
          setDevices(Array.isArray(devRes.data) ? devRes.data : []);
        }
      } catch (e) {
        if (!abort) setErr(e?.response?.data?.message || e?.message || '로드 실패');
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [locationName, floorName]);

  const imageUrl = useMemo(() => {
    const url = floor?.floorplanUrl;
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  }, [floor]);

  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  const clientToPercent = useCallback((clientX, clientY) => {
    const el = imgRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = clamp01((clientX - rect.left) / rect.width);
    const y = clamp01((clientY - rect.top) / rect.height);
    return { x, y };
  }, []);

  const getXY = (d) => {
    const over = changed.get(d.id);
    return over ? over : { x: d.x ?? 0.5, y: d.y ?? 0.5 };
  };

  const onMarkerMouseDown = (id, e) => { e.preventDefault(); setDraggingId(id); };
  const onMarkerTouchStart = (id) => { setDraggingId(id); };

  useEffect(() => {
    if (!draggingId) return;

    const move = (clientX, clientY) => {
      const p = clientToPercent(clientX, clientY);
      if (!p) return;
      setChanged((prev) => {
        const next = new Map(prev);
        next.set(draggingId, p);
        return next;
      });
    };
    const onMove = (e) => {
      if (e.touches && e.touches[0]) move(e.touches[0].clientX, e.touches[0].clientY);
      else move(e.clientX, e.clientY);
    };
    const onUp = () => setDraggingId(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId, clientToPercent]);

  const changedList = useMemo(() => {
    const arr = [];
    for (const d of devices) {
      const over = changed.get(d.id);
      if (over && (over.x !== d.x || over.y !== d.y)) {
        arr.push({
          id: d.id,
          x: Number(over.x.toFixed(6)),
          y: Number(over.y.toFixed(6)),
          location: location?.name,
          floor: floor?.name,
        });
      }
    }
    return arr;
  }, [devices, changed, location?.name, floor?.name]);

  const hasChanges = changedList.length > 0;

  const save = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await Promise.all(changedList.map((p) => api.patch('/admin/device/position', p)));
      setDevices((prev) =>
        prev.map((d) => {
          const nv = changed.get(d.id);
          return nv ? { ...d, x: nv.x, y: nv.y } : d;
        })
      );
      setChanged(new Map());
      alert('저장되었습니다.');
    } catch (e) {
      alert(`저장 실패: ${e?.response?.data?.message || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const revertOne = (id) => {
    setChanged((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };
  const revertAll = () => setChanged(new Map());

  if (loading) return <div className="placeholder">불러오는 중…</div>;
  if (err) return <div className="placeholder">오류: {err}</div>;
  if (!location || !floor) return <div className="placeholder">위치/층 정보를 찾을 수 없습니다.</div>;
  if (!imageUrl) return <div className="placeholder">도면 이미지가 없습니다.</div>;

  return (
    <div className="editor-canvas-full">
      {/* 🔵 도면 영역 (풀스크린) */}
      <div className="floorplan-viewer-map">
        <TransformWrapper
          panning={{ disabled: Boolean(draggingId) }}
          doubleClick={{ disabled: true }}
          pinch={{ disabled: false }}
          wheel={{ step: 0.2 }}
        >
          <TransformComponent>
            <div className="zoom-image-wrapper-map">
              <img
                ref={imgRef}
                src={imageUrl}
                alt={`${location.name} ${floor.name}`}
                className="floorplan-image"
                onError={() => console.error('floorplan image load failed:', imageUrl)}
              />
              {devices.map((d) => {
                const { x, y } = getXY(d);
                const isChanged = Boolean(changed.get(d.id));
                return (
                  <div
                    key={d.id}
                    className="device-marker"
                    style={{
                      left: `${x * 100}%`,
                      top: `${y * 100}%`,
                      outline: isChanged ? '2px solid #2c3e50' : 'none',
                    }}
                    title={`${d.id} (${(x * 100).toFixed(1)}%, ${(y * 100).toFixed(1)}%)`}
                    onMouseDown={(e) => onMarkerMouseDown(d.id, e)}
                    onTouchStart={() => onMarkerTouchStart(d.id)}
                  >
                    <img src="/images/LES_normal_ICON.png" alt="" className="device-marker-img" />
                  </div>
                );
              })}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* 🟣 우상단 가로 바 */}
      <div className="editor-topbar">
        <div className="editor-topbar__title">
          <strong>{location.name}</strong>
          <span>{floor.name} · {devices.length}대</span>
        </div>

        <div className="editor-topbar__actions">
          <button
            className="btn btn-ghost"
            onClick={() =>
              navigate(`/floorplan/${encodeURIComponent(locationName)}/${encodeURIComponent(floorName)}`)
            }
            title="도면으로 돌아가기"
          >
            ←
          </button>

          <button
            className="btn btn-plain"
            disabled={!hasChanges || saving}
            onClick={() => setShowChanges((v) => !v)}
            title="변경 목록 보기/숨기기"
          >
            변경 {changedList.length}
          </button>

          <button className="btn btn-outline" disabled={!hasChanges || saving} onClick={revertAll}>
            변경 취소
          </button>

          <button className="btn btn-primary" disabled={!hasChanges || saving} onClick={save}>
            {saving ? '저장 중…' : `저장 (${changedList.length})`}
          </button>
        </div>

        {/* 드롭다운 변경목록 */}
        {showChanges && (
          <div className="editor-topbar__dropdown" onMouseLeave={() => setShowChanges(false)}>
            {changedList.length === 0 ? (
              <div className="editor-empty">변경 없음</div>
            ) : (
              <ul className="editor-change-list">
                {changedList.map((c) => (
                  <li key={c.id} className="editor-change-item">
                    <div className="editor-change-id">{c.id}</div>
                    <div className="editor-change-actions">
                      <button className="btn btn-plain" onClick={() => revertOne(c.id)}>되돌리기</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
